import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { WindowsLogo, AppleLogo, LinuxLogo, DownloadSimple, ShieldCheck, CheckCircle, Warning } from "@phosphor-icons/react";

function detectOS() {
  const s = `${navigator.userAgent} ${navigator.platform}`.toLowerCase();
  if (s.includes("win")) return "win";
  if (s.includes("mac") || s.includes("iphone") || s.includes("ipad")) return "mac";
  if (s.includes("linux") || s.includes("android")) return "linux";
  return "win";
}

const HELPERS = {
  win: { label: "Windows", icon: WindowsLogo, ext: "bat", run: "Double-click the downloaded controlly-support.bat and keep the window open." },
  mac: { label: "macOS", icon: AppleLogo, ext: "command", run: "Open the downloaded controlly-support.command (allow it in System Settings → Privacy if prompted) and keep the window open." },
  linux: { label: "Linux", icon: LinuxLogo, ext: "sh", run: "Run: bash ~/Downloads/controlly-support.sh — keep the terminal open." },
};

export default function SupportJoin() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [os, setOs] = useState(detectOS());
  const [connected, setConnected] = useState(false);

  useEffect(() => { document.title = "Get Remote Support — Controlly"; }, []);

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const res = await api.get(`/support/join/${token}`);
        if (!live) return;
        setState({ loading: false, ...res.data });
        if (res.data.connected) setConnected(true);
      } catch (e) {
        if (!live) return;
        setState({ loading: false, error: e?.response?.data?.detail || "This support link is invalid or expired." });
      }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { live = false; clearInterval(t); };
  }, [token]);

  const helper = HELPERS[os];
  const dlUrl = `${API}/support/helper/${token}.${helper.ext}`;

  return (
    <div className="min-h-screen bg-[#0b1220] text-white flex flex-col items-center justify-center px-4 py-10">
      <div className="flex items-center gap-2 mb-8">
        <img src="/controlly-icon.png" alt="Controlly" className="w-9 h-9" />
        <span className="font-heading text-lg font-extrabold tracking-tight">Controlly</span>
      </div>

      <div className="w-full max-w-md bg-zinc-900 border border-white/10 rounded-2xl p-7" data-testid="support-join-card">
        {state.loading ? (
          <div className="text-center text-zinc-400 py-8 animate-pulse">Loading…</div>
        ) : state.error ? (
          <div className="text-center py-6" data-testid="support-join-error">
            <Warning size={40} className="text-amber-400 mx-auto mb-4" weight="fill" />
            <h1 className="font-heading text-xl font-bold">Link unavailable</h1>
            <p className="text-sm text-zinc-400 mt-2">{state.error}</p>
          </div>
        ) : connected ? (
          <div className="text-center py-6" data-testid="support-join-connected">
            <CheckCircle size={48} className="text-emerald-400 mx-auto mb-4" weight="fill" />
            <h1 className="font-heading text-2xl font-bold">You're connected</h1>
            <p className="text-sm text-zinc-400 mt-2">
              {state.helper_by ? `${state.helper_by} can now see and help with your screen.` : "Your technician can now see and help with your screen."}
              {" "}Keep the helper window open. A banner shows whenever someone is viewing.
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Get remote help</h1>
            <p className="text-sm text-zinc-400 mt-2">
              {state.helper_by ? <><span className="text-zinc-200 font-medium">{state.helper_by}</span> is ready to help you.</> : "Your technician is ready to help you."}
              {" "}Download and run the one-time helper below — no account needed, and it removes itself when you're done.
            </p>

            <div className="flex gap-1.5 mt-6 mb-4">
              {Object.entries(HELPERS).map(([key, h]) => {
                const Icon = h.icon;
                return (
                  <button key={key} data-testid={`support-os-${key}`} onClick={() => setOs(key)}
                    className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-medium border transition-colors ${
                      os === key ? "border-blue-500 bg-blue-500/10 text-white" : "border-white/10 text-zinc-400 hover:bg-zinc-800"}`}>
                    <Icon size={17} weight="fill" /> {h.label}
                  </button>
                );
              })}
            </div>

            <a href={dlUrl} data-testid="support-download-button"
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors">
              <DownloadSimple size={20} weight="bold" /> Download helper for {helper.label}
            </a>

            <ol className="mt-5 space-y-2 text-sm text-zinc-300 list-decimal list-inside">
              <li>{helper.run}</li>
              <li>It takes a minute the first time (it sets things up), then connects you automatically.</li>
              <li>This page will update to <span className="text-emerald-400">connected</span> when you're linked.</li>
            </ol>

            <div className="mt-5 flex items-start gap-2 text-xs text-zinc-500 border-t border-white/10 pt-4">
              <ShieldCheck size={16} className="text-emerald-500/80 shrink-0 mt-0.5" weight="fill" />
              <span>You stay in control. A visible banner appears whenever your screen is being viewed, and closing the helper window ends the session and deletes it.</span>
            </div>
          </>
        )}
      </div>
      <p className="text-xs text-zinc-600 mt-6">Powered by Controlly · Remote Desktop, Simplified.</p>
    </div>
  );
}
